import { Injectable, inject } from '@angular/core';
import { Observable, defer, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { VirtualAssistantService } from 'src/shared/services/api/virtual-assistant.service';
import type {
  AssistantChatContext,
  AssistantChatData,
  AssistantChatRequest,
  ChatStreamEvent,
} from '../../voice/types/voice-assistant.types';
import type { AssistantIntentResult, WorkflowEnrichmentHint } from '../models/assistant-intent.model';
import { AssistantConversationStateService } from './assistant-conversation-state.service';
import { AssistantIntentService } from './assistant-intent.service';
import { AssistantWorkflowEngineService } from './assistant-workflow-engine.service';
import { logAssistantDebug, logWorkflowError } from '../utils/assistant-debug.util';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AssistantMessageRouterService {
  private readonly virtualAssistant = inject(VirtualAssistantService);
  private readonly conv = inject(AssistantConversationStateService);
  private readonly intent = inject(AssistantIntentService);
  private readonly workflow = inject(AssistantWorkflowEngineService);
  private readonly translate = inject(TranslateService);

  /** Workflow bootstrap / session-end must not use streaming general chat. */
  requiresWorkflowRoute(intent: AssistantIntentResult): boolean {
    return (
      intent.kind === 'educational_generation' || intent.kind === 'session_end'
    );
  }

  /** Voice-first educational entry (Quick vs Custom) without opening hybrid form. */
  bootstrapEducationalArtifact(
    artifact: string
  ): Observable<AssistantChatData> {
    return this.workflow.bootstrapFromIntent(artifact).pipe(
      switchMap(turn => this.flattenWorkflowTurn(turn)),
      catchError(() =>
        of({
          response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
          action: null,
          metadata: { workflow: true } as Record<string, unknown>,
        } as AssistantChatData)
      )
    );
  }

  /** Non-stream general chat (no intent re-classification). */
  chatGeneral(
    trimmed: string,
    buildContext: () => AssistantChatContext
  ): Observable<AssistantChatData> {
    logAssistantDebug('FINAL_PROMPT', {
      message: trimmed,
      historyLength: this.conv.getCompactHistory().length,
      streaming: false,
    });
    return this.chat(buildContext, trimmed);
  }

  /** Exit an active workflow and answer via general chat (non-stream). */
  exitWorkflowAndChat(
    trimmed: string,
    buildContext: () => AssistantChatContext
  ): Observable<AssistantChatData> {
    this.resetActiveWorkflow();
    logAssistantDebug('ACTION_EXECUTED', { action: 'exitWorkflowAndChat', message: trimmed });
    return this.chatGeneral(trimmed, buildContext);
  }

  resetActiveWorkflow(): void {
    if (this.conv.workflowSessionActive()) {
      this.conv.resetEducationalSession();
      this.workflow.endSession();
    }
  }

  route(
    trimmed: string,
    buildContext: () => AssistantChatContext
  ): Observable<AssistantChatData> {
    if (this.conv.workflowSessionActive() && this.conv.isSessionTimedOut()) {
      this.conv.resetEducationalSession();
      this.workflow.endSession();
      return of({
        response: this.translate.instant('voiceAssistant.workflow.sessionTimeout'),
        action: null,
        metadata: {},
      } as AssistantChatData);
    }

    return this.intent.classify(trimmed, false).pipe(
      switchMap((intent: AssistantIntentResult) => {
        if (intent.kind === 'session_end') {
          this.conv.resetEducationalSession();
          this.workflow.endSession();
          return of({
            response: this.translate.instant('voiceAssistant.workflow.sessionCancelled'),
            action: null,
            metadata: {},
          } as AssistantChatData);
        }

        if (intent.kind === 'educational_generation' && intent.artifactHint) {
          if (
            this.conv.workflowSessionActive() &&
            intent.artifactHint === this.conv.sessionArtifact() &&
            this.conv.generationPathMode() !== 'unset'
          ) {
            logAssistantDebug('ACTIVE_WORKFLOW', {
              action: 'resume_instead_of_bootstrap',
              artifact: intent.artifactHint,
            });
            return this.routeWorkflowContinue(trimmed);
          }
          this.resetActiveWorkflow();
          return this.workflow.bootstrapFromIntent(intent.artifactHint).pipe(
            switchMap(turn => this.flattenWorkflowTurn(turn)),
            catchError(() =>
              of({
                response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
                action: null,
                metadata: {},
              } as AssistantChatData)
            )
          );
        }

        if (intent.kind === 'workflow_continue') {
          return this.routeWorkflowContinue(trimmed, intent.workflowEnrichment);
        }

        if (intent.kind === 'general_chat' || intent.kind === 'unknown') {
          return this.chat(buildContext, trimmed);
        }

        return this.chat(buildContext, trimmed);
      }),
      catchError(err => {
        logWorkflowError({
          workflowId: this.conv.sessionArtifact(),
          currentStep: this.conv.currentSlotKey(),
          userInput: trimmed,
          workflowData: this.conv.snapshot(),
          error: err,
        });
        return of({
          response: this.translate.instant('voiceAssistant.workflow.unexpectedError'),
          action: null,
          metadata: {
            workflow: true,
            workflowError: true,
            ...(!environment.production
              ? { debugError: String(err instanceof Error ? err.message : err) }
              : {}),
          } as Record<string, unknown>,
        } as AssistantChatData);
      })
    );
  }

  /** Slot-first workflow turn (voice/text during guided session). */
  routeWorkflowContinue(
    trimmed: string,
    enrichment?: WorkflowEnrichmentHint
  ): Observable<AssistantChatData> {
    if (this.conv.workflowSessionActive() && this.conv.isSessionTimedOut()) {
      this.conv.resetEducationalSession();
      this.workflow.endSession();
      return of({
        response: this.translate.instant('voiceAssistant.workflow.sessionTimeout'),
        action: null,
        metadata: {},
      } as AssistantChatData);
    }
    logAssistantDebug('ACTION_EXECUTED', {
      action: 'routeWorkflowContinue',
      message: trimmed,
      enrichment,
      slot: this.conv.currentSlotKey(),
      artifact: this.conv.sessionArtifact(),
    });
    return defer(() => {
      try {
        const turn = this.workflow.continueWorkflow(trimmed, enrichment);
        return this.flattenWorkflowTurn(turn);
      } catch (err) {
        logWorkflowError({
          workflowId: this.conv.sessionArtifact(),
          currentStep: this.conv.currentSlotKey(),
          userInput: trimmed,
          workflowData: this.conv.snapshot(),
          error: err,
        });
        return of({
          response: this.translate.instant('voiceAssistant.workflow.unexpectedError'),
          action: null,
          metadata: {
            workflow: true,
            workflowError: true,
            ...(!environment.production
              ? { debugError: String(err instanceof Error ? err.message : err) }
              : {}),
          } as Record<string, unknown>,
        } as AssistantChatData);
      }
    });
  }

  private flattenWorkflowTurn(turn: {
    response: string;
    speak: boolean;
    speechText?: string;
    followUp$?: Observable<{
      response: string;
      speak: boolean;
      speechText?: string;
    }>;
  }): Observable<AssistantChatData> {
    const base = (r: string, speechText?: string): AssistantChatData => ({
      response: r,
      speechText,
      action: null,
      metadata: {
        workflow: true,
        ...(speechText ? { speechText } : {}),
      } as Record<string, unknown>,
    });
    if (turn.followUp$) {
      return turn.followUp$.pipe(
        map(t =>
          base(
            t.response || turn.response,
            t.speechText ?? turn.speechText
          )
        ),
        catchError(() =>
          of(base(this.translate.instant('voiceAssistant.workflow.sessionEnded')))
        )
      );
    }
    return of(base(turn.response, turn.speechText));
  }

  private buildChatPayload(
    buildContext: () => AssistantChatContext,
    trimmed: string
  ): AssistantChatRequest {
    const history = this.conv.getCompactHistory();
    const historyForApi =
      history.length > 0 &&
      history[history.length - 1]?.role === 'user' &&
      history[history.length - 1]?.content === trimmed
        ? history.slice(0, -1)
        : history;

    return {
      message: trimmed,
      history: historyForApi.length ? historyForApi : undefined,
      context: buildContext(),
    };
  }

  /** Streaming general chat (SSE). */
  chatStream(
    buildContext: () => AssistantChatContext,
    trimmed: string,
    onEvent: (evt: ChatStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<AssistantChatData> {
    const payload = this.buildChatPayload(buildContext, trimmed);
    logAssistantDebug('FINAL_PROMPT', {
      message: trimmed,
      historyLength: payload.history?.length ?? 0,
      streaming: true,
    });
    return this.virtualAssistant.chatStream(payload, onEvent, signal);
  }

  private chat(
    buildContext: () => AssistantChatContext,
    trimmed: string
  ): Observable<AssistantChatData> {
    return this.virtualAssistant
      .chat(this.buildChatPayload(buildContext, trimmed))
      .pipe(
        catchError(() =>
          of({
            response: this.translate.instant('toast.genericError'),
            action: null,
            metadata: {},
          } as AssistantChatData)
        )
      );
  }
}
